import {useEffect, useRef, useState} from 'react';

/**
 * useAsyncStorageValue
 *
 * Loads a value from an async storage backend (IndexedDB via `localforage`,
 * or any other store whose read returns a `Promise`) once on mount, and
 * returns it once resolved — `undefined` until then.
 *
 * This stays a mount effect on purpose: unlike a synchronous store such as
 * `localStorage` (read during render with `useSyncExternalStore` instead), an
 * async storage read has no render-time equivalent — there is no better home
 * for it than a purpose-built hook (CLAUDE.md's "React useEffect Policy").
 *
 * `loadValue` is captured once, the same way the deprecated `useEffectOnce`
 * captures its callback: a ref read inside the effect keeps this a genuine
 * mount-only read regardless of the caller passing a fresh function identity
 * on every render.
 *
 * @param loadValue - Reads the persisted value, e.g. `() => myStorage.get()`
 * @returns The loaded value, or undefined while it is still loading
 */
const useAsyncStorageValue = <T>(
  loadValue: () => Promise<T>,
): T | undefined => {
  const [value, setValue] = useState<T>();
  const loadValueRef = useRef(loadValue);

  // effect:audited — loads a persisted value from an async store (IndexedDB /
  // localforage and similar), a genuine async read with no render-time
  // equivalent.
  useEffect(() => {
    let cancelled = false;

    loadValueRef.current().then((loadedValue) => {
      if (!cancelled) setValue(loadedValue);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return value;
};

export default useAsyncStorageValue;
